import { TFunction } from 'next-i18next';
import { Funktion } from 'types';
import { PersonDto } from 'types/personen';
import { UnternehmenDto } from 'types/unternehmen';

interface MetaTagContent
{
  title: string;
  description: string;
  keywords: string;
}

export function getUnternehmenMetaTagContent(t: TFunction, unternehmen: UnternehmenDto): MetaTagContent
{
  const geschäftsführung: string | null = (() =>
  {
    if (!unternehmen.leitungsorgane || unternehmen.leitungsorgane.items.length === 0)
    {
      return null;
    }

    const gf = unternehmen.leitungsorgane.items.find((p) => p.funktion === Funktion.Geschäftsführer && p.änderungsArt !== 'Gelöscht');
    return gf ? gf.ref.name : null;
  })();

  const description: Array<string | null> = [
    t('profiles:meta.unternehmen.info', { value: unternehmen.name }),
    geschäftsführung ? t('profiles:meta.unternehmen.geschäftsführung', { value: geschäftsführung }) : null,
    unternehmen.adresse ? t('profiles:meta.unternehmen.adresse', { value: unternehmen.adresse.full }) : null,
    t('profiles:meta.daily'),
    t('profiles:meta.historical'),
  ];

  return {
    title: `▷ ${unternehmen.name} (${t('formats:fbNr', { value: unternehmen.fbNr })})`,
    description: description.filter(d => d !== null).join(' ✓ '),
    keywords: t('profiles:meta.unternehmen.keywords', { name: unternehmen.name, address: unternehmen.adresse }),
  };
}

export function getPersonMetaTagContent(t: TFunction, person: PersonDto): MetaTagContent
{
  const beteiligung: string | null = (() =>
  {
    if (Object.keys(person.netzwerk).length === 0)
    {
      return null;
    }

    const sorted = Object.entries(person.netzwerk).sort((a, b) => b[0].localeCompare(a[0]));
    const firstActive = sorted[0][1].beteiligungen.find(b => b.änderungsArt !== 'Gelöscht');
    return firstActive ? firstActive.ref.name : null;
  })();

  const funktion: string | null = (() =>
  {
    if (person.leitungsorgane !== null && person.leitungsorgane.items.length > 0)
    {
      const leitungsorgan = person.leitungsorgane.items.find((p) => p.änderungsArt !== 'Gelöscht');
  
      if (leitungsorgan)
      {
        return `${leitungsorgan.ref.name} ${t(`funktionen:list.${leitungsorgan.funktion}`)}`;
      }
    }

    if (person.kontrollorgane !== null && person.kontrollorgane.items.length > 0)
    {
      const kontrollorgan = person.kontrollorgane.items.find((p) => p.änderungsArt !== 'Gelöscht');
  
      if (kontrollorgan)
      {
        return `${kontrollorgan.ref.name} ${t(`funktionen:list.${kontrollorgan.funktion}`)}`;
      }
    }

    return null;
  })();

  const description: Array<string | null> = [
    t('profiles:meta.personen.info', { value: person.hauptPersonenDaten.fullName }),
    beteiligung ? t('profiles:meta.personen.beteiligung', { value: beteiligung }) : t('profiles:meta.personen.keineBeteiligung'),
    funktion ? t('profiles:meta.personen.funktion', { value: funktion }) : null,
    t('profiles:meta.daily'),
    t('profiles:meta.historical'),
  ];

  return {
    title: `▷ ${person.hauptPersonenDaten.fullName}`,
    description: description.filter(d => d !== null).join(' ✓ '),
    keywords: t('profiles:meta.personen.keywords', { name: person.hauptPersonenDaten.fullName }),
  };
}